import { UseQueryResult, UseSuspenseQueryResult } from "@tanstack/react-query";
import client, { throwCaughtError, moreMemoryApiClient } from "../client";
import { base64ToBlob, CURR_VERSION } from "@/lib";
import { env } from "@/lib";
import type { TwoNDeviceConfig, TwoNSyncResult } from "@/types";
import {
  useBuildingQuery,
  useBuildingSuspenseQuery,
} from "@/components/RouterSetup";

const isDevEnv = env("VITE_ENVIRONMENT") === "dev";

export const useVersionNumber = (): UseQueryResult<string> => {
  return useBuildingQuery({
    queryKey: ["version"],
    queryFn: async () => {
      if (isDevEnv) {
        return CURR_VERSION;
      }
      try {
        const response = await fetch("/version.json", { cache: "no-store" });
        const { version } = await response.json();
        return version;
      } catch (error) {
        console.error(error);
        return CURR_VERSION;
      }
    },
    refetchInterval: isDevEnv ? false : 1000 * 60 * 5, // 5 mins
  });
};

export const uploadImage = async (image: string, userName?: string) => {
  return await moreMemoryApiClient.post("/uploadImage", {
    image,
    ...(userName ? { userName } : {}),
  });
};

/**
 * Returns "" when the upload fails. Callers that fall back to a previously
 * saved image rely on this; if the caller needs to know *which* uploads failed
 * and why, use `uploadImageWithResult` instead.
 */
export const handleUploadImage = async (
  encodedImage: string,
  onError?: (error: unknown) => void,
): Promise<string> => {
  try {
    const imgUpload = await uploadImage(encodedImage);
    return imgUpload.data.url;
  } catch (error) {
    console.error(error);
    onError?.(error);
    return "";
  }
};

export type ImageUploadResult =
  { ok: true; url: string } | { ok: false; error: unknown };

/**
 * Upload variant that reports failure instead of collapsing it to "". Lets a
 * caller uploading several images tell the user exactly which ones failed.
 */
export const uploadImageWithResult = async (
  encodedImage: string,
): Promise<ImageUploadResult> => {
  try {
    const imgUpload = await uploadImage(encodedImage);
    return { ok: true, url: imgUpload.data.url };
  } catch (error) {
    console.error(error);
    return { ok: false, error };
  }
};

export const uploadFile = async (
  file: string,
  companyName: string,
): Promise<string | null> => {
  try {
    const res = await client.post("/uploadFile", {
      file,
      companyName,
    });
    return res.data.fileUrl;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const sendSMSMessage = async ({
  messageBody,
  recipientLanguage,
  quickMessage,
  visit,
}: {
  messageBody: string;
  recipientLanguage?: string;
  quickMessage?: boolean;
  visit: {
    companyVisit: boolean;
    visitId: string;
  };
}) => {
  return await client.post("/sendSms", {
    messageBody,
    recipientLanguage,
    quickMessage,
    visit,
  });
};

export const getIntercomHMAC = async (): Promise<string | undefined> => {
  try {
    const response = await client.post("/createHmac");
    return response.data.hash;
  } catch (error) {
    throwCaughtError(error);
  }
};

export const getImageFunction = async (path?: string): Promise<string> => {
  if (!path || path.startsWith("blob:")) return "";
  try {
    // Stored paths can themselves be URLs carrying a query string, so the value
    // has to be encoded or everything after its first "&" is parsed as a
    // separate param and the backend receives a truncated path.
    const response = await client.get(
      `/getImage?path=${encodeURIComponent(path)}`,
    );
    return response.data.url || "";
  } catch (error) {
    console.error(error);
    return "";
  }
};

export const getImageOptions = (path?: string) => {
  return {
    queryKey: ["image", path ?? "no-path"],
    queryFn: () => getImageFunction(path),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
  };
};

export const useImage = (path?: string): UseSuspenseQueryResult<string> => {
  return useBuildingSuspenseQuery(getImageOptions(path));
};

export const getBlobFromUrl = async (url: string) => {
  try {
    const response = await client.get(`/getBlobFromUrl?url=${url}`, {
      responseType: "blob",
    });

    const contentType = (response.headers["content-type"] ||
      "application/octet-stream") as string;
    let blob: Blob;

    const rawData = response.data;
    const textData = await (rawData instanceof Blob ? rawData.text() : rawData);

    if (textData.includes("base64")) {
      blob = base64ToBlob(textData, contentType);
    } else {
      blob = rawData;
    }

    const fileExtension = contentType.split("/")[1]?.split("+")[0] || "bin";

    return { blob, fileExtension };
  } catch (error) {
    throwCaughtError(error);
  }
};

export const translateText = async (
  text: string,
  targetLanguage?: "en" | "no",
): Promise<string> => {
  try {
    if (!text) return "";
    const targetUrl =
      targetLanguage === "no" ? "/translateToNorwegian" : "/translate";
    const response = await client.post(targetUrl, { text });
    return response.data?.translatedText ?? "";
  } catch (error) {
    throwCaughtError(error);
    return "";
  }
};

export const runOneMonthNoticeManually = async () => {
  await client.post("/runQuarterlyUserDeletionJob");
};
export const runTwoWeekNoticeManually = async () => {
  await client.post("/runTwoWeeksInactivityJob");
};
export const runQuarterlyUserPurgeManually = async () => {
  await client.post("/runOneMonthInactivityJob");
};
export const logToBackend = async (message: string, error: object) => {
  try {
    await client.post("/debugLog", { message, error });
  } catch (error) {
    console.error("Failed to log message to backend:", error);
  }
};

export const syncDefigoUsersManually = async (dryRun: boolean) => {
  await moreMemoryApiClient.post(`/syncDefigoUsersManual?dryRun=${dryRun}`);
};

export const sync2NDirectoryManually = async (
  dryRun: boolean,
): Promise<TwoNSyncResult[]> => {
  const response = await moreMemoryApiClient.post(
    `/sync2NDirectoryManual?dryRun=${dryRun}`,
  );
  return response.data.results;
};

export const update2NConfig = async (
  twoN: Omit<TwoNDeviceConfig, "sipAccount"> | null,
) => {
  await moreMemoryApiClient.patch("/update2NConfig", { twoN });
};

export const syncCardsInArxManually = async (dryRun: boolean) => {
  await client.post(`/syncCardsInArx?dryRun=${dryRun}`);
};

export type StarwatchGroupSyncSummary = {
  added: number;
  renamed: number;
  reactivated: number;
  removed: number;
  // Starwatch returned no roles while the catalogue had active ones
  removalsSkipped: boolean;
};

// Syncs the current building's Starwatch access groups into the catalogue.
export const syncStarwatchAccessGroupsManually =
  async (): Promise<StarwatchGroupSyncSummary> => {
    const response = await client.post("/syncStarwatchAccessGroups");
    return response.data;
  };
